import React, { ElementType, ReactNode, SyntheticEvent, useState, useRef } from 'react';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ClearIcon from '@mui/icons-material/Clear';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Paper from '@mui/material/Paper';
import { Add } from '@mui/icons-material';
import { InputAdornment, SvgIcon, SxProps } from '@mui/material';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { Typography } from '@mui/material';
import {
  AutocompleteButton,
  AutocompleteLabel,
  AutocompleteListAction,
  GroupHeader,
  GroupItems,
  StyledAutocompleteFormControl
} from './AutocompleteComponents';
import './TwAutocomplete.scss';
import TwTooltip from '../TwTooltip/TwTooltip';

export interface TwAutocompleteOption {
  id: string;
  subId?: string;
  label: string;
  subLabel?: string;
  value: unknown;
  groupBy?: string;
  tooltipTitle?: string;
  key?: string;
  endAdornmentIcon?: ReactNode;
}

export interface TwAutocompleteAction {
  action: () => void;
  label: string;
}

export interface TwAutocompleteProps {
  /**
   * Label text for the Autocomplete.
   */
  label?: string;
  /**
   * Id for the Autocomplete.
   */
  id?: string;
  /**
   * Autocomplete editable status.
   */
  disabled?: boolean;
  /**
   * Text for the placeholder, if any.
   */
  placeholder?: string;
  /**
   * Callback fired when the value is changed.
   */
  onChange?: (
    event: React.SyntheticEvent<Element, Event>,
    value: TwAutocompleteOption | null
  ) => void;
  /**
   * Callback fired on input blur.
   */
  onBlur?: (event: React.FocusEvent<HTMLDivElement, Element>) => void;
  /**
   * Callback fired when the input value is changed.
   */
  onInputChange?: (event: SyntheticEvent<Element, Event>, value: string | null) => void;
  /**
   * Action to show at the end of the options list.
   */
  listAction?: TwAutocompleteAction | undefined;
  /**
   * Autocomplete name.
   */
  name: string;
  /**
   * Classname for the container.
   */
  className?: string;
  /**
   * Sx props for the container.
   */
  sx?: SxProps;
  /**
   * Autocomplete option list.
   */
  optionList: TwAutocompleteOption[];
  /**
   * Autocomplete sort flag.
   */
  sortList?: boolean;
  /**
   * Autocomplete value.
   */
  value: TwAutocompleteOption | null | undefined | unknown;
  /**
   * Autocomplete default value.
   * @default null
   * @optional
   * @type TwAutocompleteOption
   * @see https://material-ui.com/api/autocomplete/#props
   *
   */
  defaultValue?: TwAutocompleteOption | null | undefined | unknown;
  /**
   * Autocomplete has error flag.
   */
  error?: boolean;
  /**
   * Helper text for the errors.
   */
  helperText?: string | undefined;
  /**
   * Label icon
   */
  labelIcon?: ReactNode;
  /**
   * Autocomplete option for input.
   */
  autoComplete?: string;
  /**
   * If true, user input doesn't need to match an option
   * but is considered as valid value
   */
  freeText?: boolean;
  /**
   * if true, 'x' icon is shown
   */
  enableClearIcon?: boolean;
  /**
   * if true, clear icon is disabled
   *
   */
  disableClearable?: boolean;
  /**
   * if true, No option message is not showing
   */
  disableNoOption?: boolean;
  /**
   * onKeyDown event handler
   */
  onKeyDown?: (e: React.KeyboardEvent<HTMLDivElement>) => void;
  /**
   * Use as filter
   */
  useAsFilter?: boolean;

  filterOptions?: any;
  /**
   * Accept selection of multiple items
   */
  multiple?: boolean;
  /**
   * Remove selected options from select options
   */
  filterSelectedOptions?: boolean;
  /**
   * Limit the number of tags to be shown
   */
  limitTags?: number;
  /**
   * Typeahead filter has grouped sections
   */
  grouped?: boolean;
  /**
   * Typeahead filter render options
   */
  renderOption?: (props: React.HTMLAttributes<HTMLLIElement>, option: any) => ReactNode;
  readonly?: boolean;
  showEndAdornment?: boolean;
  onReadonlyAdornmentClick?: () => void;
  highlight?: boolean;
  /**
   * Disable portal: If true, the Popper content will be under the DOM hierarchy
   * of the parent component. (default: false)
   */
  disablePortal?: boolean;

  /**
   * Tooltip title
   */
  tooltipTitle?: string;
  /**
   * auto focus
   */
  autoFocus?: boolean;
}

export interface TwRenderOptionProps {
  props: React.HTMLAttributes<HTMLLIElement>;
  useAsFilter?: boolean;
  option: TwAutocompleteOption;
  icon?: ElementType<any>;
  endAdornmentIcon?: ReactNode;
}

export const RenderOption: React.FC<TwRenderOptionProps> = ({
  props,
  useAsFilter,
  option,
  icon,
  endAdornmentIcon
}) => {
  return (
    <Box
      component="span"
      key={option.id}
      {...props}
      sx={useAsFilter ? { padding: '6px !important' } : { padding: 'inherit' }}
    >
      <Stack
        direction="row"
        spacing={1}
        justifyContent="flex-start"
        alignItems="flex-start"
        sx={{ width: '100%' }}
      >
        {useAsFilter && icon && (
          <SvgIcon component={icon} inheritViewBox style={{ color: '#9CA3AF' }} />
        )}

        <TwTooltip title={option.tooltipTitle} placement="top">
          <Stack
            spacing={0}
            direction="column"
            justifyContent="space-between"
            alignItems="flex-start"
            sx={{ width: '100%', position: 'relative' }}
          >
            <Typography variant="subtitle2" fontWeight="400" color="#1F2937" fontSize="14px">
              {option.label}
            </Typography>

            {option.subLabel && (
              <Typography
                variant="subtitle2"
                fontWeight="400"
                color="#9CA3AF"
                fontSize={useAsFilter ? '12px' : '14px'}
              >
                {option.subLabel}
              </Typography>
            )}

            {endAdornmentIcon && (
              <Box sx={{ position: 'absolute', right: '0' }}>{endAdornmentIcon}</Box>
            )}
          </Stack>
        </TwTooltip>
      </Stack>
    </Box>
  );
};

const TwAutocomplete: React.FC<TwAutocompleteProps> = ({
  id,
  label,
  name,
  value,
  defaultValue = null,
  disabled,
  optionList,
  sortList = true,
  placeholder,
  helperText,
  className,
  sx,
  error,
  onChange,
  onBlur,
  onInputChange,
  listAction,
  labelIcon,
  autoComplete,
  filterOptions,
  freeText,
  onKeyDown,
  readonly,
  highlight,
  onReadonlyAdornmentClick,
  disableNoOption,
  enableClearIcon = false,
  disableClearable = false,
  useAsFilter,
  multiple,
  filterSelectedOptions,
  limitTags = -1,
  grouped,
  disablePortal = false,
  renderOption,
  tooltipTitle,
  autoFocus = false,
  showEndAdornment = true
}) => {
  const [found, setFound] = useState(false);
  const blurTimeout = useRef<NodeJS.Timeout | null>(null);

  const OptionListAction: React.FC<{ children?: ReactNode }> = ({
    children,
    ...other
  }): JSX.Element => {
    return (
      <Paper {...other}>
        <AutocompleteButton
          disableNoOption={disableNoOption}
          onClick={(e): void => {
            e.stopPropagation();
            e.preventDefault();
            listAction?.action();
          }}
          onMouseDown={(e): void => {
            e.stopPropagation();
            e.preventDefault();
          }}
        >
          <AutocompleteListAction className="tid-autocomplete__list-action">
            <Add /> {listAction?.label}
          </AutocompleteListAction>
        </AutocompleteButton>
        {children}
      </Paper>
    );
  };

  const handleInputClick = (): void => {
    if (blurTimeout.current) {
      clearTimeout(blurTimeout.current);
    }
  };

  const handleInputBlur = (e: React.FocusEvent<HTMLDivElement>): void => {
    blurTimeout.current = setTimeout(() => {
      if (onBlur) {
        onBlur(e);
      }
    }, 150);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    if (!found && onKeyDown) {
      onKeyDown(e);
    }
  };

  const groupByFunc = (option: TwAutocompleteOption | null | undefined | unknown): string =>
    (option as TwAutocompleteOption).groupBy || 'Others';

  return (
    <StyledAutocompleteFormControl
      fullWidth
      className={!(multiple && filterSelectedOptions) ? `${className} tid-autocomplete` : ``}
      sx={{
        marginBottom: '8px',
        ...sx
      }}
    >
      {label && (
        <AutocompleteLabel className="tid-autocomplete__label" htmlFor={id}>
          <span>{label}</span>
          {!!labelIcon && labelIcon}
        </AutocompleteLabel>
      )}

      <Autocomplete
        freeSolo={freeText}
        multiple={multiple}
        filterSelectedOptions={filterSelectedOptions}
        limitTags={limitTags}
        autoSelect
        forcePopupIcon={useAsFilter ? false : true}
        disabled={disabled}
        clearOnBlur={true}
        id={id}
        options={
          sortList
            ? optionList.sort((a, b) =>
                grouped && a.groupBy && b.groupBy
                  ? b.groupBy?.localeCompare(a.groupBy)
                  : a.label?.localeCompare(b.label)
              )
            : optionList
        }
        groupBy={grouped ? groupByFunc : undefined}
        filterOptions={filterOptions}
        isOptionEqualToValue={(option, value): boolean => {
          option &&
            setFound((option as TwAutocompleteOption).id === (value as TwAutocompleteOption).id);
          return (option as TwAutocompleteOption).id === (value as TwAutocompleteOption).id;
        }}
        popupIcon={<ExpandMoreIcon />}
        classes={{
          inputRoot: 'tw-autocomplete__default',
          popupIndicator: 'tw-autocomplete__icon',
          popupIndicatorOpen: 'tw-autocomplete__iconOpen',
          popper:
            disableNoOption && useAsFilter
              ? 'tw-autocomplete__popper tw-autocomplete__popper--noOption'
              : 'tw-autocomplete__popper',
          option: 'tw-autocomplete__option tid-autocomplete__option',
          noOptions: disableNoOption
            ? 'tw-autocomplete__disableNoOption '
            : 'tw-autocomplete__noOption',
          clearIndicator: !useAsFilter
            ? `tw-autocomplete__iconClear ${enableClearIcon && 'tw-autocomplete__iconClear-flex'}`
            : `tw-autocomplete__filter-iconClear ${
                enableClearIcon && 'tw-autocomplete__iconClear-flex'
              }`
        }}
        ChipProps={{ deleteIcon: <ClearIcon /> }}
        value={value}
        defaultValue={defaultValue}
        onInputChange={onInputChange}
        onChange={(e, value): void => {
          onChange && onChange(e, (value as TwAutocompleteOption) || null);
        }}
        onClick={handleInputClick}
        onBlur={handleInputBlur}
        readOnly={readonly}
        disableClearable={disableClearable}
        renderInput={(params): JSX.Element => {
          return (
            <TwTooltip title={tooltipTitle} placement="top">
              <TextField
                {...params}
                name={name}
                placeholder={placeholder}
                error={error}
                helperText={helperText}
                autoComplete={autoComplete}
                autoFocus={autoFocus}
                InputProps={{
                  ...params.InputProps,
                  endAdornment:
                    readonly && showEndAdornment ? (
                      <InputAdornment
                        position="end"
                        sx={{ cursor: 'pointer', marginRight: '-20px' }}
                        onClick={onReadonlyAdornmentClick}
                      >
                        <EditIcon sx={{ fill: '#9CA3AF', fontSize: '18px' }} />
                      </InputAdornment>
                    ) : showEndAdornment ? (
                      params.InputProps.endAdornment
                    ) : null
                }}
                sx={{
                  backgroundColor: readonly ? 'transparent' : undefined,
                  border: highlight ? '1px solid #FCA5A5' : undefined,
                  borderRadius: '4px'
                }}
              />
            </TwTooltip>
          );
        }}
        PaperComponent={listAction?.action && OptionListAction}
        onKeyDown={handleKeyDown}
        disablePortal={disablePortal}
        renderOption={
          renderOption
            ? renderOption
            : (props, option): ReactNode => {
                return (
                  <RenderOption
                    props={props}
                    option={option as TwAutocompleteOption}
                    key={(option as TwAutocompleteOption).id}
                    endAdornmentIcon={(option as TwAutocompleteOption).endAdornmentIcon}
                  />
                );
              }
        }
        renderGroup={(params): ReactNode => (
          <li>
            <GroupHeader>{params.group}</GroupHeader>
            <GroupItems>{params.children}</GroupItems>
          </li>
        )}
      />
    </StyledAutocompleteFormControl>
  );
};

TwAutocomplete.defaultProps = {
  disabled: false,
  error: false
};

export default TwAutocomplete;
